import { DEFAULT_CURRENCY } from '@/core/currency/constants';

interface FormatPricePartsOptions {
  short?: boolean;
  locale?: string;
}

/**
 * How many minor units make up one major unit of the currency: 100 for USD,
 * 1 for zero-decimal currencies like JPY.
 */
export function minorUnitFactor(currency: string = DEFAULT_CURRENCY) {
  const { minimumFractionDigits } = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).resolvedOptions();

  return Math.pow(10, minimumFractionDigits || 0);
}

export function toMinorUnits(amount: string, currency?: string) {
  return Math.round(
    Number.parseFloat(amount || '0') * minorUnitFactor(currency)
  );
}

function formatPriceParts(
  amount: number | string,
  currency: string = DEFAULT_CURRENCY,
  options: FormatPricePartsOptions = {}
) {
  const value = typeof amount === 'string' ? Number.parseFloat(amount) : amount;

  const locale = options?.locale ?? 'en-US';
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
  });

  const parts = formatter.formatToParts(value / minorUnitFactor(currency));

  const currencyPart = parts.find((part) => part.type === 'currency')?.value;

  let amountPart = parts
    .filter((part) => part.type !== 'currency')
    .map((part) => part.value)
    .join('');

  // If shortened, remove decimal point and trailing zeroes
  if (options?.short) {
    amountPart = amountPart.replace(/(\.0+)$/, '');
  }

  return {
    currency: currencyPart,
    amount: amountPart,
  };
}

export type FormatPriceOptions = FormatPricePartsOptions;

export function formatPrice(
  amount: number | string,
  currency: string = DEFAULT_CURRENCY,
  options: FormatPriceOptions = {}
) {
  const parts = formatPriceParts(amount, currency, options);
  return [parts.currency, parts.amount].filter(Boolean).join('').trim();
}

export type FormatAmountOptions = FormatPricePartsOptions;

export function formatAmount(
  amount: number | string,
  currency: string = DEFAULT_CURRENCY,
  options: FormatAmountOptions = {}
) {
  const parts = formatPriceParts(amount, currency, options);
  return parts.amount.trim();
}

export interface FormatCurrencyOptions {
  locale?: string;
}

export function formatCurrency(
  currency: string,
  options: FormatCurrencyOptions = {}
) {
  const code = currency.toUpperCase();

  const locale = options?.locale ?? 'en-US';
  const currencyNames = new Intl.DisplayNames(locale, { type: 'currency' });

  const { currency: currencySymbol } = formatPriceParts(0, currency, options);

  return {
    code,
    symbol: currencySymbol ?? code,
    displayName: currencyNames.of(currency.toUpperCase()),
  };
}
